import Box from '@mui/material/Box';
import Button from '@mui/material/Button';
import Chip from '@mui/material/Chip';
import Stack from '@mui/material/Stack';
import Typography from '@mui/material/Typography';
import { Bell, CheckCheck } from 'lucide-react';
import { useNavigate } from 'react-router-dom';

import { EmptyState, ErrorState, LoadingState } from '@/presentation/components/index.js';
import type { NotificationController } from '@/presentation/controllers/notification-controller.js';
import { useApp } from '@/presentation/hooks/use-app.js';
import { useNotifications } from '@/presentation/hooks/use-notifications.js';
import { toDevelopmentDetail } from '@/shared/constants/routes.js';
import { formatRelative } from '@/shared/utils/format-date.js';

interface Props {
    controller: NotificationController;
}

export function NotificationsPage({ controller }: Props) {
    const { translate } = useApp();
    const navigate = useNavigate();
    const { notifications, unreadCount, isLoading, error, filter, setFilter, markRead, markAllRead, reload } = useNotifications(controller);

    if (isLoading && notifications.length === 0) {
        return <LoadingState />;
    }
    if (error) {
        return <ErrorState message={error} onRetry={reload} />;
    }

    const filters: Array<{ key: 'all' | 'unread' | 'read'; label: string }> = [
        { key: 'all', label: translate('notifications.filterAll') },
        { key: 'unread', label: translate('notifications.filterUnread') },
        { key: 'read', label: translate('notifications.filterRead') }
    ];

    return (
        <Box sx={{ p: 3 }}>
            <Stack direction="row" justifyContent="space-between" alignItems="center" sx={{ mb: 3 }}>
                <Box>
                    <Typography variant="h5" fontWeight={700}>
                        {translate('notifications.title')}
                    </Typography>
                    {unreadCount > 0 && <Chip label={translate('notifications.unreadCount', { count: unreadCount })} color="error" size="small" sx={{ mt: 0.5 }} />}
                </Box>
                {unreadCount > 0 && (
                    <Button startIcon={<CheckCheck size={16} />} onClick={markAllRead} size="small">
                        {translate('notifications.markAllRead')}
                    </Button>
                )}
            </Stack>
            <Stack direction="row" spacing={1} sx={{ mb: 2 }}>
                {filters.map((f) => (
                    <Button
                        key={f.key}
                        size="small"
                        variant={filter === f.key ? 'contained' : 'outlined'}
                        onClick={() => setFilter(f.key)}
                        sx={filter === f.key ? { color: 'white' } : undefined}
                    >
                        {f.label}
                    </Button>
                ))}
            </Stack>
            {notifications.length === 0 ? (
                <Box sx={{ display: 'flex', alignItems: 'center', justifyContent: 'center', minHeight: 'calc(100vh - 280px)' }}>
                    <EmptyState icon={<Bell size={28} />} title={translate('notifications.noNotificationsFound')} titleSx={{ color: 'text.disabled' }} />
                </Box>
            ) : (
                <Stack spacing={1}>
                    {notifications.map((n) => (
                        <Box key={n.id} sx={{ p: 2, borderRadius: 2, border: 1, borderColor: 'divider', bgcolor: n.readAt ? 'transparent' : 'action.hover' }}>
                            <Stack direction="row" spacing={1.5} alignItems="flex-start">
                                <Box sx={{ width: 8, height: 8, borderRadius: '50%', bgcolor: n.readAt ? 'text.disabled' : 'primary.main', mt: 0.5, flexShrink: 0 }} />
                                <Box sx={{ flex: 1, minWidth: 0 }}>
                                    <Typography variant="body2" fontWeight={600}>
                                        {n.title}
                                    </Typography>
                                    <Typography variant="caption" color="text.secondary" sx={{ display: 'block', mt: 0.5 }}>
                                        {n.body}
                                    </Typography>
                                    <Stack direction="row" spacing={2} alignItems="center" sx={{ mt: 1 }}>
                                        <Typography variant="caption" color="text.disabled">
                                            {formatRelative(n.createdAt, translate)}
                                        </Typography>
                                        {n.development && (
                                            <Button size="small" onClick={() => navigate(toDevelopmentDetail(n.development!.id))}>
                                                {translate('notifications.openDev')}
                                            </Button>
                                        )}
                                        {!n.readAt && (
                                            <Button size="small" onClick={() => markRead(n.id)} sx={{ textTransform: 'none' }}>
                                                {translate('notifications.markRead')}
                                            </Button>
                                        )}
                                    </Stack>
                                </Box>
                            </Stack>
                        </Box>
                    ))}
                </Stack>
            )}
        </Box>
    );
}
